import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import type { Session, User } from '@supabase/supabase-js'
import { useQueryClient } from '@tanstack/react-query'
import { qk } from '@/lib/query-keys'
import { supabase } from '@/lib/supabase'

type AuthContextValue = {
  session: Session | null
  user: User | null
  /** Verdadeiro até a sessão salva no navegador ser lida. */
  loading: boolean
  /** `local`: só limpa este navegador (ex.: após excluir a conta, quando a sessão já não existe no servidor). */
  signOut: (options?: { local?: boolean }) => Promise<void>
}

const AuthContext = createContext<AuthContextValue | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient()
  const [session, setSession] = useState<Session | null>(null)
  const [loading, setLoading] = useState(true)
  const userIdRef = useRef<string | null>(null)

  useEffect(() => {
    let active = true

    const apply = (next: Session | null) => {
      if (!active) return
      const nextUserId = next?.user.id ?? null
      if (nextUserId !== userIdRef.current) {
        // Outro usuário (ou ninguém): descarta tudo que era da sessão anterior.
        queryClient.removeQueries({ queryKey: qk.me.all })
        queryClient.invalidateQueries({ queryKey: qk.admin.all })
        userIdRef.current = nextUserId
      }
      setSession(next)
      setLoading(false)
    }

    supabase.auth.getSession().then(({ data }) => apply(data.session))

    // Não chame outras funções do Supabase dentro deste callback (risco de travamento).
    const { data: subscription } = supabase.auth.onAuthStateChange((_event, next) => apply(next))

    return () => {
      active = false
      subscription.subscription.unsubscribe()
    }
  }, [queryClient])

  const signOut = useCallback(async (options?: { local?: boolean }) => {
    await supabase.auth.signOut(options?.local ? { scope: 'local' } : undefined)
    queryClient.removeQueries({ queryKey: qk.me.all })
    queryClient.removeQueries({ queryKey: qk.admin.all })
    // Rascunhos podem revelar opinião política: não ficam no navegador após sair.
    try {
      window.localStorage.removeItem('pa:proposal-draft')
      window.sessionStorage.clear()
    } catch {
      // armazenamento indisponível
    }
  }, [queryClient])

  const value = useMemo<AuthContextValue>(
    () => ({ session, user: session?.user ?? null, loading, signOut }),
    [session, loading, signOut],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext)
  if (!context) throw new Error('useAuth precisa estar dentro de <AuthProvider>.')
  return context
}
